/**
 * Forward projections — what a partnership is likely to be worth.
 *
 * One module, two audiences. The creator's earnings calculator and the brand's
 * cost calculator call the SAME functions, so the number a brand is told it
 * will pay and the number a creator is told they will earn are arithmetically
 * the same figure seen from opposite sides. They cannot disagree, which is what
 * makes it safe to show both on one page.
 *
 * Everything here is an ESTIMATE from assumptions the user supplies. It is kept
 * strictly separate from `commission.js`, which computes money actually owed —
 * conflating the two is how a projection ends up on an invoice.
 */

import { calculateCommission, isRecurring } from './commission.js';

/**
 * @typedef {object} FunnelAssumptions
 * @property {number} [audience]        Followers or list size.
 * @property {number} [reachRatePct]    Share of the audience that sees a post.
 * @property {number} [clickRatePct]    Share of those reached who click.
 * @property {number} [clicks]          Supply directly to skip the two above.
 * @property {number} conversionRatePct Share of clicks that convert.
 * @property {number} orderAmountCents  Average order value.
 * @property {number} [posts]           How many times the creator posts.
 */

/** Clicks implied by an audience and a funnel, or the clicks given directly. */
export function projectClicks({
  audience = 0,
  reachRatePct = 100,
  clickRatePct = 0,
  clicks = null,
  posts = 1,
} = {}) {
  if (clicks !== null && clicks !== undefined) return Math.max(0, Math.round(clicks));
  const reached = (audience * reachRatePct) / 100;
  return Math.max(0, Math.round(((reached * clickRatePct) / 100) * Math.max(1, posts)));
}

/**
 * A full projection for one set of terms.
 *
 * Returns integer cents throughout and `null` for any metric whose denominator
 * is empty — the same rule the live dashboards follow, so a projection with no
 * clicks reads as unknown rather than as a confident zero.
 */
// Public projections show the commission itself; a fee is applied only where a
// signed-in creator's or brand's own half is shown (feeBps passed in).
export function projectPartnership(terms, assumptions = {}, { feeBps = 0 } = {}) {
  const {
    conversionRatePct = 0,
    orderAmountCents = 0,
    /** Share of customers who survive to the next billing cycle. */
    cycleRetentionPct = 100,
  } = assumptions;

  const clicks = projectClicks(assumptions);
  const conversions = Math.max(0, (clicks * conversionRatePct) / 100);

  // Per-conversion economics come straight from the real commission function.
  const perConversion = calculateCommission(terms, { orderAmountCents, feeBps });

  const recurring = isRecurring(terms);
  const contractedCycles = recurring ? terms.recurringMonths : 1;

  // Nobody keeps every subscriber for the whole window, so the expected number
  // of paid cycles is a geometric sum rather than the contracted length. At
  // 100% retention this collapses to the contracted cycles, which is the
  // brand's maximum exposure and the creator's best case — and the UI says so.
  const effectiveCycles = recurring
    ? expectedCycles(contractedCycles, cycleRetentionPct / 100)
    : 1;

  const grossCents = Math.round(perConversion.grossAmountCents * conversions * effectiveCycles);
  const feeCents = Math.round(perConversion.platformFeeCents * conversions * effectiveCycles);
  const brandFeeCents = Math.round(perConversion.brandFeeCents * conversions * effectiveCycles);
  const revenueCents = Math.round(orderAmountCents * conversions * effectiveCycles);

  return {
    clicks,
    conversions: Math.round(conversions * 100) / 100,
    effectiveCycles: Math.round(effectiveCycles * 100) / 100,
    contractedCycles,

    perConversion: {
      ...perConversion,
      // What one acquired customer is worth over the whole window.
      lifetimeGrossCents: perConversion.grossAmountCents * contractedCycles,
      lifetimeNetCents: perConversion.netAmountCents * contractedCycles,
    },

    // Creator's view.
    grossCents,
    feeCents,
    netCents: grossCents - feeCents,
    epc: clicks > 0 ? grossCents / 100 / clicks : null,

    // Brand's view: the commission plus the brand's half of Affily's fee —
    // the commission alone where no fee is given (public pages).
    brandFeeCents,
    costCents: grossCents + brandFeeCents,
    revenueCents,
    costPerAcquisitionCents: conversions >= 1 ? Math.round(grossCents / conversions) : null,
    /** Commission as a share of the revenue it generated, in basis points. */
    costOfSaleBps: revenueCents > 0 ? Math.round((grossCents / revenueCents) * 10_000) : null,
    returnOnSpend: grossCents > 0 ? revenueCents / grossCents : null,
  };
}

/**
 * Expected paid cycles given a per-cycle retention rate.
 *
 * A customer always pays cycle 1, then survives each subsequent cycle with
 * probability r. The expectation is the geometric sum 1 + r + r² + … + r^(n-1).
 */
export function expectedCycles(contractedCycles, retention) {
  if (contractedCycles <= 1) return 1;
  if (retention >= 1) return contractedCycles;
  if (retention <= 0) return 1;
  return (1 - retention ** contractedCycles) / (1 - retention);
}

/**
 * A brand's committed forward liability from ONE month of acquisition.
 *
 * The number recurring programs make easy to overlook: customers acquired this
 * month keep billing, so this month's commission decision is a payment schedule
 * rather than a one-off expense.
 */
export function projectRecurringLiability(terms, assumptions = {}, options = {}) {
  const projection = projectPartnership(
    terms,
    { ...assumptions, cycleRetentionPct: 100 },
    options,
  );

  if (!isRecurring(terms)) {
    return { isRecurring: false, cycles: [], totalCents: projection.grossCents };
  }

  const retention = (assumptions.cycleRetentionPct ?? 100) / 100;
  const perCustomerCents = projection.perConversion.grossAmountCents;

  const cycles = [];
  let remaining = projection.conversions;
  for (let cycle = 1; cycle <= terms.recurringMonths; cycle++) {
    cycles.push({
      cycle,
      customers: Math.round(remaining * 100) / 100,
      costCents: Math.round(perCustomerCents * remaining),
    });
    // Everyone pays their first cycle; each cycle after that loses the churned
    // share of whoever is left, which is how churn actually compounds.
    remaining *= retention;
  }

  return {
    isRecurring: true,
    perCycleCents: Math.round(perCustomerCents * projection.conversions),
    cycles,
    totalCents: cycles.reduce((sum, c) => sum + c.costCents, 0),
  };
}
